import React, { useState, useEffect } from 'react';
import SideNavbar from "../components/Navbar/SideNavbar";
import Header from "../components/Header/Header";
import axiosInstance from '../../core/services/axiosConfig';
import { apis } from '../../core/services/params';

interface ProductInfo {
    mainImageLink?: string;
    productUrl?: string;
    salesPrice?: number;
}

interface Report {
    created_at: string;
    updated_at: string;
    history: string[];
    message_count: number;
    product_id: string;
    product_info?: ProductInfo;
    session_id: string;
    user_id: string;
}

interface Product {
    chat_count: number;
    last_chat: string;
    product_id: string;
    unique_sessions: number;
    unique_users: number;
    product_info?: ProductInfo;
}

interface Widgets {
    description: string;
    generated_at: string;
    periods: {
        last_30_days: {
            date_range: { end: string; start: string };
            period: string;
            products: Product[];
            total_products: number;
        };
        last_7_days: {
            date_range: { end: string; start: string };
            period: string;
            products: Product[];
            total_products: number;
        };
        today: {
            date_range: { end: string; start: string };
            period: string;
            products: Product[];
            total_products: number;
        };
    };
    title: string;
}

interface DashboardResponse {
    data: {
        reports: Report[];
        pagination: {
            current_page: number;
            has_next: boolean;
            has_prev: boolean;
            items_per_page: number;
            total_items: number;
            total_pages: number;
        };
        filters: {
            end_date: string;
            product_id: string | null;
            session_id: string | null;
            sort_by: string;
            sort_order: string;
            start_date: string;
            user_id: string | null;
        };
        widgets?: Widgets;
    };
    message: string;
    status: boolean;
}

interface ConversationMessage {
    role: 'user' | 'bot';
    text: string;
    timestamp?: string;
}

const ProductChatbotAnalysis: React.FC = () => {
    const [records, setRecords] = useState<Report[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [widgets, setWidgets] = useState<Widgets | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters
    const today = new Date().toISOString().split('T')[0];
    const [productIdFilter, setProductIdFilter] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');
    const [sessionIdFilter, setSessionIdFilter] = useState('');
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    // Conversation modal
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [conversationModal, setConversationModal] = useState(false);
    const [conversationData, setConversationData] = useState<ConversationMessage[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError('');

        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: limit.toString(),
                sort_by: 'created_at',
                sort_order: 'desc',
            });

            if (productIdFilter.trim()) params.append('product_id', productIdFilter.trim());
            if (userIdFilter.trim()) params.append('user_id', userIdFilter.trim());
            if (sessionIdFilter.trim()) params.append('session_id', sessionIdFilter.trim());
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);

            const response = await axiosInstance.get<DashboardResponse>(
                `${apis.FETCH_PRODUCT_CHAT_HISTORY}?${params.toString()}`
            );

            if (response.data && response.data.status) {
                setRecords(response.data.data.reports || []);
                setTotalPages(response.data.data.pagination.total_pages || 0);
                setTotalRecords(response.data.data.pagination.total_items || 0);
                setWidgets(response.data.data.widgets || null);
            } else {
                setRecords([]);
                setError('No data found for the selected criteria');
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to fetch dashboard data');
            setRecords([]);
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        fetchDashboardData();
    };

    const handleReset = () => {
        setProductIdFilter('');
        setUserIdFilter('');
        setSessionIdFilter('');
        setStartDate(today);
        setEndDate(today);
        setCurrentPage(1);
        fetchDashboardData();
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        try {
            return dateString.replace(' GMT', '');
        } catch {
            return dateString;
        }
    };

    const parseConversationHistory = (history: string[]): ConversationMessage[] => {
        const messages: ConversationMessage[] = [];
        
        if (!history || history.length === 0) return messages;
        
        try {
            history.forEach((entry, index) => {
                if (typeof entry === 'string') {
                    // First try to parse as JSON
                    try {
                        const parsed = JSON.parse(entry);
                        if (parsed.user_query) {
                            messages.push({
                                role: 'user',
                                text: parsed.user_query,
                                timestamp: formatDate(parsed.timestamp || '')
                            });
                        }
                        if (parsed.chatbot_response) {
                            messages.push({
                                role: 'bot',
                                text: parsed.chatbot_response,
                                timestamp: formatDate(parsed.timestamp || '')
                            });
                        }
                    } catch (parseError) {
                        // If JSON parsing fails, try parsing as simple text format
                        if (entry.startsWith('user:')) {
                            const text = entry.replace('user:', '').trim();
                            if (text) {
                                messages.push({
                                    role: 'user',
                                    text: text
                                });
                            }
                        } else if (entry.startsWith('model:')) {
                            const text = entry.replace('model:', '').trim();
                            if (text) {
                                messages.push({
                                    role: 'bot',
                                    text: text
                                });
                            }
                        } else if (entry.startsWith('bot:')) {
                            const text = entry.replace('bot:', '').trim();
                            if (text) {
                                messages.push({
                                    role: 'bot',
                                    text: text
                                });
                            }
                        } else {
                            // If no prefix found, try to determine role based on position or content
                            messages.push({
                                role: index % 2 === 0 ? 'user' : 'bot',
                                text: entry.trim()
                            });
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Error processing conversation history:', error);
        }
        
        return messages;
    };

    const viewConversation = (report: Report) => {
        setSelectedReport(report);
        setConversationData(parseConversationHistory(report.history));
        setConversationModal(true);
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line
    }, [currentPage, limit]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (dropdownOpen && !target.closest('.dropdown-container')) {
                setDropdownOpen(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    <div className="p-4 bg-gray-50 min-h-screen">
                        {/* Header */}
                        <div className="mb-4">
                            <h1 className="text-xl font-bold text-gray-800 mb-1">GenAI Product Chatbot Analysis</h1>
                            <p className="text-sm text-gray-600">Manage and analyze chatbot interactions</p>
                        </div>

                        {/* Widgets Section */}
                        {widgets && (
                            <div className="mb-6">
                                <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl border border-indigo-200 p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                                                <svg className="w-6 h-6 mr-3 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                                {widgets.title}
                                            </h2>
                                            <p className="text-gray-600 mt-1">{widgets.description}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-gray-500">
                                                Generated at: {new Date(widgets.generated_at).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {/* Today */}
                                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mr-3">
                                                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900">Today</h3>
                                                        <p className="text-sm text-gray-500">{widgets.periods.today.date_range.start}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {widgets.periods.today.products.slice(0, 3).map((product, idx) => {
                                                    const recordMatch = records.find(r => r.product_id === product.product_id);
                                                    const productInfo = recordMatch?.product_info || product.product_info;
                                                    return (
                                                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                                                    <span className="text-green-600 font-semibold text-sm">#{idx + 1}</span>
                                                                </div>
                                                                {productInfo?.mainImageLink ? (
                                                                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 mr-3">
                                                                        <img 
                                                                            src={productInfo.mainImageLink} 
                                                                            alt={product.product_id}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                const target = e.target as HTMLImageElement;
                                                                                target.style.display = 'none';
                                                                                target.nextElementSibling?.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                        <div className="w-full h-full bg-purple-100 rounded-lg flex items-center justify-center hidden">
                                                                            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                                                                                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                ) : null}
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-col">
                                                                        {productInfo?.productUrl ? (
                                                                            <a 
                                                                                href={productInfo.productUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-semibold text-green-600 hover:text-green-800 text-sm transition-colors underline-offset-2 hover:underline truncate"
                                                                            >
                                                                                {product.product_id}
                                                                            </a>
                                                                        ) : (
                                                                            <div className="font-semibold text-gray-900 text-sm truncate">{product.product_id}</div>
                                                                        )}
                                                                        <div className="flex items-center space-x-2">
                                                                            <div className="text-xs text-gray-500">{product.unique_users} users • {product.unique_sessions} sessions</div>
                                                                            {productInfo?.salesPrice && (
                                                                                <div className="text-xs font-medium text-green-600">₹{productInfo.salesPrice}</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right flex-shrink-0">
                                                                <div className="text-lg font-bold text-green-600">{product.chat_count}</div>
                                                                <div className="text-xs text-gray-500">chats</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Last 7 Days */}
                                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-3">
                                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900">Last 7 Days</h3>
                                                        <p className="text-sm text-gray-500">{widgets.periods.last_7_days.date_range.start} - {widgets.periods.last_7_days.date_range.end}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {widgets.periods.last_7_days.products.slice(0, 3).map((product, idx) => {
                                                    const recordMatch = records.find(r => r.product_id === product.product_id);
                                                    const productInfo = recordMatch?.product_info || product.product_info;
                                                    return (
                                                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                                                    <span className="text-blue-600 font-semibold text-sm">#{idx + 1}</span>
                                                                </div>
                                                                {productInfo?.mainImageLink ? (
                                                                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 mr-3">
                                                                        <img 
                                                                            src={productInfo.mainImageLink} 
                                                                            alt={product.product_id}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                const target = e.target as HTMLImageElement;
                                                                                target.style.display = 'none';
                                                                                target.nextElementSibling?.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                        <div className="w-full h-full bg-purple-100 rounded-lg flex items-center justify-center hidden">
                                                                            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                                                                                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                ) : null}
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-col">
                                                                        {productInfo?.productUrl ? (
                                                                            <a 
                                                                                href={productInfo.productUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-semibold text-blue-600 hover:text-blue-800 text-sm transition-colors underline-offset-2 hover:underline truncate"
                                                                            >
                                                                                {product.product_id}
                                                                            </a>
                                                                        ) : (
                                                                            <div className="font-semibold text-gray-900 text-sm truncate">{product.product_id}</div>
                                                                        )}
                                                                        <div className="flex items-center space-x-2">
                                                                            <div className="text-xs text-gray-500">{product.unique_users} users • {product.unique_sessions} sessions</div>
                                                                            {productInfo?.salesPrice && (
                                                                                <div className="text-xs font-medium text-blue-600">₹{productInfo.salesPrice}</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right flex-shrink-0">
                                                                <div className="text-lg font-bold text-blue-600">{product.chat_count}</div>
                                                                <div className="text-xs text-gray-500">chats</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Last 30 Days */}
                                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mr-3">
                                                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900">Last 30 Days</h3>
                                                        <p className="text-sm text-gray-500">{widgets.periods.last_30_days.date_range.start} - {widgets.periods.last_30_days.date_range.end}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {widgets.periods.last_30_days.products.slice(0, 3).map((product, idx) => {
                                                    const recordMatch = records.find(r => r.product_id === product.product_id);
                                                    const productInfo = recordMatch?.product_info || product.product_info;
                                                    return (
                                                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                                                    <span className="text-purple-600 font-semibold text-sm">#{idx + 1}</span>
                                                                </div>
                                                                {productInfo?.mainImageLink ? (
                                                                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 mr-3">
                                                                        <img 
                                                                            src={productInfo.mainImageLink} 
                                                                            alt={product.product_id}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                const target = e.target as HTMLImageElement;
                                                                                target.style.display = 'none';
                                                                                target.nextElementSibling?.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                        <div className="w-full h-full bg-purple-100 rounded-lg flex items-center justify-center hidden">
                                                                            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                                                                                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                ) : null}
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-col">
                                                                        {productInfo?.productUrl ? (
                                                                            <a 
                                                                                href={productInfo.productUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-semibold text-purple-600 hover:text-purple-800 text-sm transition-colors underline-offset-2 hover:underline truncate"
                                                                            >
                                                                                {product.product_id}
                                                                            </a>
                                                                        ) : (
                                                                            <div className="font-semibold text-gray-900 text-sm truncate">{product.product_id}</div>
                                                                        )}
                                                                        <div className="flex items-center space-x-2">
                                                                            <div className="text-xs text-gray-500">{product.unique_users} users • {product.unique_sessions} sessions</div>
                                                                            {productInfo?.salesPrice && (
                                                                                <div className="text-xs font-medium text-purple-600">₹{productInfo.salesPrice}</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right flex-shrink-0">
                                                                <div className="text-lg font-bold text-purple-600">{product.chat_count}</div>
                                                                <div className="text-xs text-gray-500">chats</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Filters Section */}
                        <div className="bg-white rounded-xl shadow-sm border mb-4">
                            <div className="p-4 border-b border-gray-200">
                                <h2 className="text-base font-semibold text-gray-800 mb-3">Filters & Search</h2>
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                            Product ID
                                        </label>
                                        <input
                                            type="text"
                                            value={productIdFilter}
                                            onChange={(e) => setProductIdFilter(e.target.value)}
                                            placeholder="Enter product ID..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            User ID
                                        </label>
                                        <input
                                            type="text"
                                            value={userIdFilter}
                                            onChange={(e) => setUserIdFilter(e.target.value)}
                                            placeholder="Enter user ID..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            Session ID
                                        </label>
                                        <input
                                            type="text"
                                            value={sessionIdFilter}
                                            onChange={(e) => setSessionIdFilter(e.target.value)}
                                            placeholder="Enter session ID..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                                            </svg>
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                                            </svg>
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-3 mt-4">
                                    <button
                                        onClick={handleSearch}
                                        disabled={loading}
                                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors space-x-2 font-medium text-sm"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                <span>Search</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleReset}
                                        className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors space-x-2 font-medium text-sm"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        <span>Reset</span>
                                    </button>
                                </div>
                            </div>

                            {/* Stats Row */}
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-6">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            <span className="text-sm text-gray-600">
                                                Total Records: <span className="font-semibold text-gray-900">{totalRecords.toLocaleString()}</span>
                                            </span>
                                        </div>
                                        {totalPages > 0 && (
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <span className="text-sm text-gray-600">
                                                    Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <label className="text-sm text-gray-700">Show:</label>
                                        <select
                                            value={limit}
                                            onChange={(e) => {
                                                setLimit(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value={5}>5 rows</option>
                                            <option value={10}>10 rows</option>
                                            <option value={25}>25 rows</option>
                                            <option value={50}>50 rows</option>
                                            <option value={100}>100 rows</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Error Display */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-sm font-medium text-red-800">Error</h3>
                                        <p className="text-sm text-red-700 mt-1">{error}</p>
                                    </div>
                                    <button
                                        onClick={() => setError('')}
                                        className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Table */}
                        <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-4">
                            <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                                <table className="w-full">
                                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/6">
                                                <div className="flex items-center space-x-2">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                    <span>Product ID</span>
                                                </div>
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/6">
                                                <div className="flex items-center space-x-2">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <span>User ID</span>
                                                </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/8">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                    </svg>
                                                    <span>Messages</span>
                                                </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/4">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span>Created At</span>
                                                </div>
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/6">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-12 text-center">
                                                    <div className="flex flex-col items-center justify-center space-y-3">
                                                        <div className="relative">
                                                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200"></div>
                                                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-base font-medium text-gray-700">Loading chatbot data...</p>
                                                            <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : records.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-12 text-center">
                                                    <div className="flex flex-col items-center justify-center space-y-3">
                                                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                                                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                            </svg>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-lg font-medium text-gray-700">No conversations found</p>
                                                            <p className="text-sm text-gray-500">Try adjusting your search filters or date range</p>
                                                        </div>
                                                        {(productIdFilter || userIdFilter || sessionIdFilter || startDate || endDate) && (
                                                            <button
                                                                onClick={handleReset}
                                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                            >
                                                                Clear Filters
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            records.map((record, index) => (
                                                <tr key={record.session_id || index} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <div className="flex items-center space-x-3">
                                                            {record.product_info?.mainImageLink ? (
                                                                <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                                                                    <img 
                                                                        src={record.product_info.mainImageLink} 
                                                                        alt={record.product_id}
                                                                        className="w-full h-full object-cover"
                                                                        onError={(e) => {
                                                                            const target = e.target as HTMLImageElement;
                                                                            target.style.display = 'none';
                                                                            target.nextElementSibling?.classList.remove('hidden');
                                                                        }}
                                                                    />
                                                                    <div className="w-full h-full bg-purple-100 rounded-lg flex items-center justify-center hidden">
                                                                        <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                    <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-center space-x-2">
                                                                        {record.product_info?.productUrl ? (
                                                                            <a 
                                                                                href={record.product_info.productUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-semibold text-blue-600 hover:text-blue-800 text-sm transition-colors underline-offset-2 hover:underline"
                                                                            >
                                                                                {record.product_id}
                                                                            </a>
                                                                        ) : (
                                                                            <div className="font-semibold text-gray-900 text-sm">{record.product_id}</div>
                                                                        )}
                                                                        <button
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(record.product_id);
                                                                                // Optional: Add a toast notification here
                                                                            }}
                                                                            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
                                                                            title="Copy Product ID"
                                                                        >
                                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2 mt-1">
                                                                        <div className="text-xs text-gray-500">Product</div>
                                                                        {record.product_info?.salesPrice && (
                                                                            <div className="text-xs font-medium text-green-600">₹{record.product_info.salesPrice}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                </svg>
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-semibold text-gray-900 text-sm">{record.user_id}</div>
                                                                <div className="text-xs text-gray-500">User</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="inline-flex items-center justify-center">
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                                </svg>
                                                                {record.message_count}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="flex items-center justify-center space-x-2">
                                                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <div className="text-center">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {formatDate(record.created_at)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="relative inline-block dropdown-container">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDropdownOpen(dropdownOpen === record.session_id ? null : record.session_id);
                                                                }}
                                                                className="inline-flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                                            >
                                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                                </svg>
                                                            </button>

                                                            {dropdownOpen === record.session_id && (
                                                                <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(null)}>
                                                                    <div
                                                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <div className="py-2">
                                                                            <button
                                                                                onClick={() => {
                                                                                    viewConversation(record);
                                                                                    setDropdownOpen(null);
                                                                                }}
                                                                                className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 w-full text-left transition-all"
                                                                            >
                                                                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                                                                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                                                    </svg>
                                                                                </div>
                                                                                <div className="min-w-0 flex-1">
                                                                                    <div className="font-semibold text-gray-900">View Conversation</div>
                                                                                    <div className="text-xs text-gray-500">Open detailed chat history</div>
                                                                                </div>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {!loading && records.length > 0 && totalPages > 1 && (
                            <div className="bg-white rounded-xl shadow-sm border p-4 sticky bottom-4">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                                        <span className="font-medium">{Math.min(currentPage * limit, totalRecords)}</span> of{' '}
                                        <span className="font-medium">{totalRecords}</span> results
                                    </div>

                                    <div className="flex items-center space-x-1">
                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                            Previous
                                        </button>

                                        <div className="flex items-center space-x-1">
                                            {(() => {
                                                const pages = [];
                                                const showPages = 5;
                                                let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                                                let endPage = Math.min(totalPages, startPage + showPages - 1);

                                                if (endPage - startPage + 1 < showPages) {
                                                    startPage = Math.max(1, endPage - showPages + 1);
                                                }

                                                if (startPage > 1) {
                                                    pages.push(
                                                        <button
                                                            key={1}
                                                            onClick={() => setCurrentPage(1)}
                                                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            1
                                                        </button>
                                                    );
                                                    if (startPage > 2) {
                                                        pages.push(
                                                            <span key="ellipsis1" className="inline-flex items-center px-3 py-2 text-sm text-gray-500">
                                                                ...
                                                            </span>
                                                        );
                                                    }
                                                }

                                                for (let page = startPage; page <= endPage; page++) {
                                                    pages.push(
                                                        <button
                                                            key={page}
                                                            onClick={() => setCurrentPage(page)}
                                                            className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${page === currentPage
                                                                ? 'bg-blue-600 text-white border border-blue-600'
                                                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                }

                                                if (endPage < totalPages) {
                                                    if (endPage < totalPages - 1) {
                                                        pages.push(
                                                            <span key="ellipsis2" className="inline-flex items-center px-3 py-2 text-sm text-gray-500">
                                                                ...
                                                            </span>
                                                        );
                                                    }
                                                    pages.push(
                                                        <button
                                                            key={totalPages}
                                                            onClick={() => setCurrentPage(totalPages)}
                                                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            {totalPages}
                                                        </button>
                                                    );
                                                }

                                                return pages;
                                            })()}
                                        </div>

                                        <button
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                                        >
                                            Next
                                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Conversation Modal */}
            {conversationModal && selectedReport && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                    <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Product Chatbot Conversation</h3>
                                    <p className="text-sm text-gray-600 flex items-center mt-1">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                        Product: {selectedReport.product_id}
                                        <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {conversationData.length} messages
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setConversationModal(false)}
                                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 bg-gray-50">
                            <div className="bg-white rounded-xl border border-gray-200 max-h-[75vh] overflow-y-auto">
                                {conversationData.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                        </div>
                                        <p className="text-lg font-medium text-gray-700">No messages found</p>
                                        <p className="text-sm text-gray-500">This conversation appears to be empty</p>
                                    </div>
                                ) : (
                                    <div className="p-6 space-y-4">
                                        {conversationData.map((msg, idx) => (
                                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${msg.role === 'user'
                                                    ? 'bg-blue-500 text-white rounded-br-md shadow-lg'
                                                    : 'bg-white border-2 border-gray-100 text-gray-800 rounded-bl-md shadow-md'
                                                    }`}>
                                                    <div className="text-sm leading-relaxed whitespace-pre-wrap">
                                                        {msg.text}
                                                    </div>

                                                    {/* Timestamp */}
                                                    {msg.timestamp && (
                                                        <div className={`text-xs mt-3 flex items-center ${msg.role === 'user' ? 'text-blue-100 justify-end' : 'text-gray-500 justify-start'
                                                            }`}>
                                                            <svg className="w-3 h-3 mr-1 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            {msg.timestamp}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-sm text-gray-500">
                                {conversationData.length > 0 && `${conversationData.length} messages loaded`}
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setConversationModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductChatbotAnalysis;